/**
 * PRODUCT CONTRACT — UI Authority
 *
 * This is the SINGLE SOURCE OF TRUTH for what the product should render.
 * Any component that deviates from this contract is a violation.
 * Verification gate: scripts/system/verify_product_contract.py
 *
 * Authority chain: Frontend-Spec (ChatGPT) → This file → Components → Gate verification
 */

// ============================================================
// SECTION 1: MODULE REGISTRY
// Canonical modules of Accrezeo OS.
// The sidebar MUST show exactly these modules in this order.
// Adding, removing, or renaming modules requires updating this contract.
// Section order: CEO Office → Agents (Sourcing / Relationships /
// Content / Analysis) → Data → Settings (footer)
// DS-DESIGN-MIGRATION: Phosphor icons replace emoji per DESIGN_LANGUAGE_SKILL.md
// ============================================================
export const MODULE_REGISTRY = [
    // CEO Office
    { id: 'ceo_office', name: 'CEO Office', path: '/ceo', icon: 'Tray', group: 'ceo', section: 'CEO Office' },

    // Agents
    { id: 'lead_gen_scout', name: 'LinkedIn Scout', path: '/agents/linkedin-scout', icon: 'LinkedinLogo', group: 'agents', section: 'Sourcing' },
    { id: 'deal_sourcing', name: 'Deal Sourcing', path: '/agents/deal-sourcing', icon: 'FunnelSimple', group: 'agents', section: 'Sourcing' },
    { id: 'founder_outreach', name: 'Founder Outreach', path: '/agents/founder-outreach', icon: 'EnvelopeSimple', group: 'agents', section: 'Relationships' },
    { id: 'linkedin_post', name: 'Post Studio', path: '/agents/linkedin-post', icon: 'ChatCircle', group: 'agents', section: 'Content', badge: { apiPath: '/api/linkedin-posts/draft/count', responseKey: 'count' } },
    { id: 'sector_intelligence', name: 'Sector Intelligence', path: '/agents/sector-intelligence', icon: 'MagnifyingGlass', group: 'agents', section: 'Analysis' },
    { id: 'deal_evaluator', name: 'Deal Evaluator', path: '/agents/deal-evaluator', icon: 'ChartLineUp', group: 'agents', section: 'Analysis' },
    { id: 'cross_sector_patterns', name: 'Cross-Sector Patterns', path: '/agents/cross-sector-patterns', icon: 'Graph', group: 'agents', section: 'Analysis' },
    { id: 'principle_library', name: 'Principle Library', path: '/agents/principle-library', icon: 'BookBookmark', group: 'agents', section: 'Analysis' },

    // Data (V7 order: KB, Founders, Startups, Investors, Campaigns, Deals, Ideas, Tags)
    { id: 'knowledge_base', name: 'Knowledge Base', path: '/knowledge-base', icon: 'Books', group: 'data', section: 'Data' },
    { id: 'founders', name: 'Founders', path: '/founders', icon: 'UsersThree', group: 'data', section: 'Data' },
    { id: 'startups', name: 'Startups', path: '/startups', icon: 'Rocket', group: 'data', section: 'Data' },
    { id: 'investors', name: 'Investors', path: '/investors', icon: 'Handshake', group: 'data', section: 'Data' },
    { id: 'campaigns', name: 'Campaigns', path: '/campaigns', icon: 'Target', group: 'data', section: 'Data' },
    { id: 'deals', name: 'Deals', path: '/deals', icon: 'Briefcase', group: 'data', section: 'Data' },
    // 'idea_bank' (/ideas) REMOVED 2026-07-30 — WI-IDEA-CLAIM-REPAIR-AND-ONE-DOOR-AGENT-PAGE-001.
    // The idea surface moved into the FIRST TAB of Post Studio (/agents/linkedin-post) by CEO
    // ruling: the whole flow lives on the agent page in the order the work moves — IDEA, then
    // PENDING, then SAVED. A separate idea screen is exactly how five ideas sat nine days
    // unseen, so the second door is closed rather than left pointing at the same place: one
    // route, one component, one entry. Post Studio is already the sidebar entry (Content
    // group), so nothing was added to replace this row.
    { id: 'tags', name: 'Tags', path: '/tags', icon: 'Tag', group: 'data', section: 'Data' },
];

// ============================================================
// SIDEBAR SECTION CONFIGURATION (V8 — Atrium-style grouped)
// Authority: docs/contracts/DESIGN_IMPLEMENTATION_CONTRACT.md §8.1
// Six groups in order: Office, Sourcing, Relationships, Content,
// Analysis, Reference. Collapsible state persisted in localStorage.
// Key shape changed from agents/data to office/sourcing/relationships/
// content/analysis/reference — triggers STATE_EPOCH 4→5 in ui-invariants.js.
// Campaigns, Deals, Idea Bank, Tags stay in the route registry (direct-URL
// access preserved) but do NOT appear in SIDEBAR_SECTIONS.
// ============================================================
export const SIDEBAR_SECTIONS = [
    {
        id: 'office',
        label: 'Office',
        collapsible: true,
        defaultExpanded: true,
        items: [
            { id: 'inbox', name: 'Inbox', path: '/ceo', icon: 'Tray', badge: { apiPath: '/api/work/inbox', responseKey: 'count' } },
            { id: 'strategy_insights', name: 'Strategy Insights', path: '/strategy-insights', icon: 'Lightbulb' },
        ],
    },
    {
        id: 'sourcing',
        label: 'Sourcing',
        collapsible: true,
        defaultExpanded: true,
        items: [
            { id: 'lead_gen_scout', name: 'LinkedIn Scout', path: '/agents/linkedin-scout', icon: 'LinkedinLogo' },
            { id: 'deal_sourcing', name: 'Deal Sourcing', path: '/agents/deal-sourcing', icon: 'FunnelSimple' },
        ],
    },
    {
        id: 'relationships',
        label: 'Relationships',
        collapsible: true,
        defaultExpanded: true,
        items: [
            { id: 'founder_outreach', name: 'Founder Outreach', path: '/agents/founder-outreach', icon: 'EnvelopeSimple' },
        ],
    },
    {
        id: 'content',
        label: 'Content',
        collapsible: true,
        defaultExpanded: true,
        items: [
            { id: 'linkedin_post', name: 'Post Studio', path: '/agents/linkedin-post', icon: 'ChatCircle', badge: { apiPath: '/api/linkedin-posts/draft/count', responseKey: 'count' } },
        ],
    },
    {
        id: 'analysis',
        label: 'Analysis',
        collapsible: true,
        defaultExpanded: true,
        items: [
            { id: 'deal_evaluator', name: 'Deal Evaluator', path: '/agents/deal-evaluator', icon: 'ChartLineUp' },
            { id: 'sector_intelligence', name: 'Sector Intelligence', path: '/agents/sector-intelligence', icon: 'MagnifyingGlass' },
            { id: 'cross_sector_patterns', name: 'Cross-Sector Patterns', path: '/agents/cross-sector-patterns', icon: 'Graph' },
            { id: 'principle_library', name: 'Principle Library', path: '/agents/principle-library', icon: 'BookBookmark' },
        ],
    },
    {
        id: 'reference',
        label: 'Reference',
        collapsible: true,
        defaultExpanded: false,
        items: [
            { id: 'knowledge_base', name: 'Knowledge Base', path: '/knowledge-base', icon: 'Books' },
            { id: 'founders', name: 'Founders', path: '/founders', icon: 'UsersThree' },
            { id: 'startups', name: 'Startups', path: '/startups', icon: 'Rocket' },
            { id: 'investors', name: 'Investors', path: '/investors', icon: 'Handshake' },
        ],
    },
];

// Settings is a fixed footer item, not a module
export const SETTINGS_ITEM = { id: 'settings', name: 'Settings', path: '/settings', icon: 'Gear' };

// Settings is a fixed footer item, not a section
export const SIDEBAR_SETTINGS = {
    id: 'settings',
    label: 'Settings',
    fixed: true,
    separator: true,
    item: SETTINGS_ITEM,
};

// ============================================================
// SECTION 2: ROUTE REGISTRY
// Every route the app MUST handle. If a route exists in code
// but not here, it's unauthorized. If it's here but not in code,
// it's missing.
// ============================================================
export const ROUTE_REGISTRY = [
    '/',
    '/ceo',
    '/strategy-insights',
    '/agents/sector-intelligence',
    '/agents/founder-outreach',
    '/agents/linkedin-post',
    '/agents/linkedin-scout',
    '/agents/deal-sourcing',
    '/agents/deal-evaluator',
    '/agents/deal-evaluator/:startup_id',
    '/agents/cross-sector-patterns',
    '/agents/principle-library',
    '/startups',
    '/founders',
    '/founders/:id',
    '/investors',
    '/campaigns',
    '/deals',
    '/knowledge-base',
    // The /ideas route was REMOVED 2026-07-30 — folded into the Post Studio Ideas tab. See the
    // MODULE_REGISTRY note above. Deliberately written WITHOUT quotes around the path:
    // verify_product_contract.py::load_contract_routes scrapes this block with
    // re.findall(r"'(/[^']*)'", ...), so a quoted path inside a comment is read as a declared
    // route and reported as missing from App.js.
    '/tags',
    '/settings',
];

// ============================================================
// NAMED ROUTE CONSTANTS
// Import these in components instead of hardcoding path strings.
// Authority: ROUTE_REGISTRY above — every path here must appear there.
// ============================================================
export const ROUTES = {
    ROOT:                     '/',
    CEO_OFFICE:               '/ceo',
    STRATEGY_INSIGHTS:        '/strategy-insights',
    SECTOR_INTELLIGENCE:      '/agents/sector-intelligence',
    FOUNDER_OUTREACH:         '/agents/founder-outreach',
    LINKEDIN_POST:            '/agents/linkedin-post',
    LINKEDIN_SCOUT:           '/agents/linkedin-scout',
    DEAL_SOURCING:            '/agents/deal-sourcing',
    DEAL_EVALUATOR:           '/agents/deal-evaluator',
    DEAL_EVALUATOR_DETAIL:    '/agents/deal-evaluator/:startup_id',
    CROSS_SECTOR_PATTERNS:    '/agents/cross-sector-patterns',
    PRINCIPLE_LIBRARY:        '/agents/principle-library',
    STARTUPS:                 '/startups',
    FOUNDERS:                 '/founders',
    FOUNDER_DETAIL:           '/founders/:id',
    INVESTORS:                '/investors',
    CAMPAIGNS:                '/campaigns',
    DEALS:                    '/deals',
    KNOWLEDGE_BASE:           '/knowledge-base',
    // IDEAS removed 2026-07-30 — the ideas surface is the first tab of LINKEDIN_POST.
    TAGS:                     '/tags',
    SETTINGS:                 '/settings',
};

// ============================================================
// NAMED API PATH CONSTANTS
// API paths used in AppLayout badge polling and lib/api.js.
// Integration domain owns lib/api.js but these constants live here
// as the UI authority for known API paths referenced in UI files.
// ============================================================
export const API_PATHS = {
    LINKEDIN_DRAFT_COUNT: '/api/linkedin-posts/draft/count',
    WORK_INBOX:           '/api/work/inbox',
    STARTUPS:             '/api/startups',
    STARTUPS_COUNTS:      '/api/startups/counts',
    FOUNDERS:             '/api/founders',
    INVESTORS:            '/api/investors',
};

// ============================================================
// SECTION 3: DESIGN TOKEN CONTRACT
// Canonical visual identity. Components MUST use these values.
// Any hardcoded hex/font that differs is a violation.
// ============================================================
export const DESIGN_TOKENS = {
    colors: {
        brand_primary: '#191970',
        bg_base: '#FAFAFB',
        bg_panel: '#F4F5F7',
        bg_card: '#FFFFFF',
        text_primary: '#1E293B',  // slate-800 — WCAG AA compliant per spec §8.4
        text_secondary: '#8E94A4',
        border: '#E5E7EB',
    },
    fonts: {
        body: 'Inter',
        heading: 'DM Sans',
    },
    sidebar: {
        width: '240px',        // Atrium-style — matches DS_SPACING.sidebarWidth (DESIGN_IMPLEMENTATION_CONTRACT §2.2)
        background: '#FFFFFF', // V7: white sidebar
    },
    cards: {
        border_radius: '16px', // rounded-2xl
        shadow_rest: 'shadow-sm',
        shadow_hover: 'shadow-md',
    },
};

// ============================================================
// SECTION 4: LAYOUT CONTRACT
// Structural rules for the app shell.
// ============================================================
export const LAYOUT_CONTRACT = {
    header: {
        must_show_app_name: true,
        app_name: 'Accrezeo OS',
        must_show_user_avatar: true,
    },
    sidebar: {
        style: 'grouped_collapsible',
        must_show_settings_footer: true,
        modules_must_match: 'MODULE_REGISTRY',
        section_order: ['Office', 'Sourcing', 'Relationships', 'Content', 'Analysis', 'Reference'],
        default_collapsed: ['Reference'],
        width: '240px',
        settings_has_separator: true,
    },
    dashboard: {
        row_1_cards: 5,  // Dealflow metrics
        row_2_cards: 4,  // Intelligence metrics
        row_3_columns: 2, // Activity + Reminders
    },
};

// ============================================================
// SECTION 5: INTEGRATION CONTRACT
// Frontend-to-backend connection requirements.
// ============================================================
export const INTEGRATION_CONTRACT = {
    backend_url_source: 'REACT_APP_BACKEND_URL',  // must come from env var
    required_api_endpoints: [
        '/api/startups',
        '/api/startups/counts',
        '/api/founders',
        '/api/investors',
        '/api/campaigns',
        '/health',
    ],
    cors_must_allow: 'CANONICAL_FRONTEND_URL from url_authority.py',
};
